import type { ImportedProduct, ProductSelection, ProductSelectionReason, SelectedProduct } from "./contracts.js";

export class ProductSelectionError extends Error {
  readonly code = "insufficient_products";
}

type ScoredProduct = SelectedProduct & { originalIndex: number };

function scoreProduct(product: ImportedProduct, originalIndex: number): ScoredProduct {
  const reasons: ProductSelectionReason[] = [];
  let score = 0;
  if (product.featured) {
    score += 40;
    reasons.push("Featured by the shop");
  }
  const shortestEdge = Math.min(product.imageWidth, product.imageHeight);
  if (shortestEdge >= 1000) {
    score += 20;
    reasons.push("High-resolution image");
  }
  const ratio = product.imageWidth / Math.max(1, product.imageHeight);
  if (ratio >= .58 && ratio <= .9) {
    score += 15;
    reasons.push("Strong portrait image");
  }
  if (product.title.length <= 52) {
    score += 10;
    reasons.push("Concise display title");
  }
  const interest = Math.min(15, Math.round(Math.log10(product.favorites + 1) * 5));
  if (interest >= 12) reasons.push("High customer interest");
  score += interest;
  return { product, score, reasons, originalIndex };
}

export function selectProductsForVideo(products: ImportedProduct[], requested = 5): ProductSelection {
  const limit = Math.max(3, Math.min(8, Math.round(requested)));
  const rejected: ProductSelection["rejected"] = [];
  const seenImages = new Set<string>();
  const candidates: ScoredProduct[] = [];

  products.forEach((product, originalIndex) => {
    if (!product.primaryImage || product.imageWidth <= 0 || product.imageHeight <= 0) {
      rejected.push({ productId: product.id, reason: "insufficient-image-data" });
      return;
    }
    const imageKey = product.primaryImage.trim().toLowerCase();
    if (seenImages.has(imageKey)) {
      rejected.push({ productId: product.id, reason: "duplicate-image" });
      return;
    }
    seenImages.add(imageKey);
    candidates.push(scoreProduct(product, originalIndex));
  });

  candidates.sort((a, b) => b.score - a.score || b.product.favorites - a.product.favorites || a.originalIndex - b.originalIndex);
  const selected = candidates.slice(0, limit).map(({ originalIndex: _originalIndex, ...item }) => item);
  for (const item of candidates.slice(limit)) rejected.push({ productId: item.product.id, reason: "selection-limit" });
  if (selected.length < 3) throw new ProductSelectionError("At least three unique products with usable images are required to create a catalogue video.");
  return { requested: limit, selected, rejected };
}
